import React from 'react';
import { motion } from 'framer-motion';
import { llmModels } from '../../data/llmModels';
import { glossaryTerms } from '../../data/glossaryTerms';
import { engagementLevels } from '../../data/engagementLevels';
import { TableContainer, ScrollIndicator, EngageGrid, EngageCard } from './styles/BackgroundTabStyles';
import { TabContainer, Section } from '../shared/TabStyles';

const BackgroundTab = () => {
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <TabContainer>
      <Section>
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <h2>What's a Large Language Model (LLM)?</h2>
          <p>Picture a super‑well‑read assistant. Built on huge neural networks—software structures loosely inspired by brain neurons—an LLM has ingested millions of books, articles and webpages. As a result, it can:</p>
          
          <ul>
            <li><strong>Write and refine.</strong> Draft emails, marketing copy, long‑form reports or press releases in any tone.</li>
            <li><strong>Digest documents.</strong> Summarise 100‑page PDFs, compare contracts clause‑by‑clause or extract data tables.</li>
            <li><strong>Code on demand.</strong> Generate, explain and debug scripts in Python, SQL, JavaScript and more.</li>
            <li><strong>Tutor & brainstorm.</strong> Break down calculus proofs, role‑play a language partner or spark product roadmaps.</li>
            <li><strong>Translate and localise</strong> across 100 + languages while preserving nuance.</li>
            <li><strong>Reason over data.</strong> Analyse spreadsheets, propose statistical tests, create draft charts.</li>
            <li><strong>Create media.</strong> With multimodal models, craft images, slide decks, voice‑overs or short videos.</li>
            <li><strong>Act autonomously.</strong> Chain tools and APIs to research, schedule tasks or write reports while you focus elsewhere.</li>
          </ul>
          
          <h3>How does it work (in one minute)?</h3>
          <ul>
            <li><strong>Pattern spotting.</strong> During training, the network sees billions of word sequences and learns which words are likely to follow which.</li>
            <li><strong>Next‑word prediction.</strong> When you prompt it, the model keeps filling in words—constantly checking what it's written so far—until it completes the answer.</li>
            <li><strong>Scaling laws.</strong> Researchers found that throwing more data, parameters and compute (GPUs/TPUs) at the network makes its language skills leap, turning it from a pocket dictionary into a global library.</li>
          </ul>
          
          <div style={{ display: 'flex', justifyContent: 'center', margin: '30px 0' }}>
            <iframe 
              width="560" 
              height="315" 
              src="https://www.youtube.com/embed/enjnRVUoH9g?si=nRhAMyNqM1sDpKKe" 
              title="YouTube video player" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allowFullScreen
            ></iframe>
          </div>
          
          <p style={{ textAlign: 'center', color: '#666', fontSize: '0.9rem', marginTop: '-15px', marginBottom: '25px' }}>
            Visualisation of a Neural Network
          </p>
          
          <h3>Why it matters to you</h3>
          <ul>
            <li><strong>AI literacy is the next "digital literacy."</strong> LLMs will soon be woven into most work and consumer apps; experimenting now means you're ready when they're everywhere.</li>
            <li><strong>Productivity on tap.</strong> Offload routine writing, research and coding so you can tackle higher‑value work.</li>
            <li><strong>Competitive edge.</strong> Colleagues who learn to delegate tasks to AI get more done; organisations that adopt it deliver faster and cheaper.</li>
            <li><strong>Creativity amplifier.</strong> Instant first drafts, idea generation and design mock‑ups free you to iterate instead of staring at a blank page.</li>
            <li><strong>Democratised expertise.</strong> Need legal phrasing, statistical advice or marketing angles? An LLM gives you a "PhD in your pocket" whenever you ask.</li>
            <li><strong>Career resilience.</strong> Understanding how to direct (and critique) AI outputs positions you for roles that won't vanish but evolve.</li>
          </ul>
        </motion.div>
      </Section>

      <Section>
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
        >
          <h2>Large Language Models (LLMs)</h2>
          <p>The AI landscape is filled with various tools for different purposes.</p>
          
          <TableContainer>
            <ScrollIndicator>
              <span>⟺</span> Scroll horizontally to see all data
            </ScrollIndicator>
            <table>
              <thead>
                <tr>
                  <th>Model</th>
                  <th>What It's Good At</th>
                  <th>When to Use It</th>
                  <th>Special Features</th>
                </tr>
              </thead>
              <tbody>
                {llmModels.map((model, index) => (
                  <tr key={index}>
                    <td>{model.name}</td>
                    <td>{model.strengths}</td>
                    <td>{model.useCase}</td>
                    <td>{model.features}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableContainer>
        </motion.div>
      </Section>

      <Section>
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.3 }}
        >
          <h2>Ways to Engage</h2>
          <p>
            There's no one-size-fits-all approach to using Large Language Models. Depending on your goal — whether it's answering a question, automating a workflow, or doing deep research — there are different ways to get value. Here's a breakdown of the most common levels of engagement:
          </p>
          <EngageGrid>
            {engagementLevels.map((lvl, i) => (
              <motion.div key={i} variants={itemVariants} whileHover={{ scale: 1.02 }}>
                <EngageCard>
                  <h3>{lvl.title}</h3>
                  <div className="subtitle">{lvl.subtitle}</div>
                  <p>{lvl.description}</p>
                </EngageCard>
              </motion.div>
            ))}
          </EngageGrid>
        </motion.div>
      </Section>

      <Section>
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.4 }}
        >
          <h2>Glossary</h2>
          <p>Key terms and definitions to help you understand the AI landscape</p>
          
          <TableContainer>
            <ScrollIndicator>
              <span>⟺</span> Scroll horizontally to see all data
            </ScrollIndicator>
            <table>
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Definition</th>
                </tr>
              </thead>
              <tbody>
                {glossaryTerms.map((item, index) => (
                  <tr key={index}>
                    <td>{item.term}</td>
                    <td>{item.definition}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableContainer>
        </motion.div>
      </Section>
    </TabContainer>
  );
};

export default BackgroundTab; 